import { useState, useEffect, useRef, useCallback, useContext } from 'react'
import axios from 'axios'
import { getAuth } from 'firebase/auth'
import { Redirect } from 'react-router-dom'
import { ArrowLeft, CircleCheck, Clock, Globe, Hash, LogOut, RefreshCcw, Server, Timer } from 'lucide-react'

import app from '../../utils/firebaseApp.js'
import { AuthContext } from '../../utils/auth.js'
import { isAdminEmail } from '../../utils/adminUsers.js'
import DevDarkModeToggle from '../../components/DevDarkModeToggle.js'
import './admin.css'

const HEALTH_URL = 'https://api.extracta.ai/test'
const HEALTH_TIMEOUT_MS = 5 * 1000

const STATUS_LABELS = {
    idle: 'Not checked',
    loading: 'Checking...',
    up: 'Operational',
    down: 'Down',
    error: 'Timed out',
}

const formatDateTime = (date) => (date ? date.toLocaleString() : '—')

export default function ServerHealth() {
    const { currentUser } = useContext(AuthContext)
    const canAccessAdmin = isAdminEmail(currentUser?.email)

    const [isLoading, setIsLoading] = useState(false)
    const [status, setStatus] = useState('idle') // 'idle' | 'loading' | 'up' | 'down' | 'error'
    const [message, setMessage] = useState('')
    const [lastCheckedAt, setLastCheckedAt] = useState(null)
    const [lastSuccessAt, setLastSuccessAt] = useState(null)
    const [responseTimeMs, setResponseTimeMs] = useState(null)
    const [httpStatus, setHttpStatus] = useState(null)

    const hasRun = useRef(false)

    const handleSignout = () => {
        getAuth(app).signOut(app).catch(() => { })
    }

    const checkHealth = useCallback(async () => {
        setIsLoading(true)
        setStatus('loading')
        setMessage('')

        const startedAt = performance.now()

        try {
            const response = await axios.get(HEALTH_URL, {
                timeout: HEALTH_TIMEOUT_MS,
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            setHttpStatus(response.status)
            if (response.status === 200) {
                setStatus('up')
                setMessage('Server is up and running')
                setLastSuccessAt(new Date())
            } else {
                setStatus('down')
                setMessage(`Unexpected status code: ${response.status}`)
            }
        } catch (error) {
            setHttpStatus(error?.response?.status ?? null)
            if (error?.code === 'ECONNABORTED') {
                setStatus('error')
                setMessage(`Server did not respond within ${HEALTH_TIMEOUT_MS / 1000}s`)
            } else {
                setStatus('down')
                setMessage(error?.message || 'Server is down or returned an error')
            }
        } finally {
            setResponseTimeMs(Math.round(performance.now() - startedAt))
            setIsLoading(false)
            setLastCheckedAt(new Date())
        }
    }, [])

    useEffect(() => {
        if (!canAccessAdmin) return
        if (hasRun.current) return
        hasRun.current = true

        const appName = process.env.REACT_APP_NAME
        document.title = `Server Health - ${appName}`

        checkHealth()
    }, [canAccessAdmin, checkHealth])

    if (!canAccessAdmin) {
        return <Redirect to="/dashboard" />
    }

    const metrics = [
        {
            label: 'Response time',
            value: isLoading || responseTimeMs === null ? '—' : `${responseTimeMs} ms`,
            note: `Timeout after ${HEALTH_TIMEOUT_MS / 1000}s`,
            icon: Timer,
        },
        {
            label: 'HTTP status',
            value: isLoading || httpStatus === null ? '—' : httpStatus,
            note: 'Expected 200',
            icon: Hash,
        },
        {
            label: 'Last checked',
            value: formatDateTime(lastCheckedAt),
            note: 'Checked on page load or retry',
            icon: Clock,
        },
        {
            label: 'Last successful',
            value: formatDateTime(lastSuccessAt),
            note: 'Since this page was opened',
            icon: CircleCheck,
        },
    ]

    return (
        <div className="extracta-admin">
            <main className="admin-page">
                <header className="admin-topbar">
                    <a href="/dashboard" className="admin-brand-link" aria-label="Back to platform dashboard">
                        <img
                            src={process.env.PUBLIC_URL + '/logo_extracta_labs_white.png'}
                            alt="Extracta LABS"
                        />
                    </a>
                    <div className="admin-topbar-actions">
                        <DevDarkModeToggle className="admin-secondary-button" />
                        <a href="/dashboard" className="admin-secondary-button">
                            <ArrowLeft className="h-4 w-4" />
                            Back to platform
                        </a>
                        <a href="/admin" className="admin-secondary-button">
                            <Server className="h-4 w-4" />
                            Admin overview
                        </a>
                        <button type="button" onClick={handleSignout} className="admin-secondary-button">
                            <LogOut className="h-4 w-4" />
                            Sign out
                        </button>
                    </div>
                </header>

                <div className="admin-shell px-4 py-6 sm:px-6 lg:px-8">
                    <div className="admin-hero">
                        <div className="admin-hero-main">
                            <a href="/admin" className="admin-kicker">
                                <Server className="h-4 w-4" />
                                Back to Admin
                            </a>
                            <h1>Server health</h1>
                            <p>Check the public API status, timeout behavior, and last successful response.</p>
                        </div>

                        <div className="admin-hero-actions">
                            <button
                                type="button"
                                onClick={checkHealth}
                                disabled={isLoading}
                                className="admin-primary-button"
                            >
                                <RefreshCcw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                                {isLoading ? 'Checking...' : 'Retry health check'}
                            </button>
                        </div>
                    </div>

                    <section className={`admin-health-status is-${status}`} aria-live="polite">
                        <span className="admin-health-dot" aria-hidden="true" />
                        <div className="admin-health-copy">
                            <p>Current status</p>
                            <h2>{STATUS_LABELS[status]}</h2>
                            <span>{message || (isLoading ? 'Waiting for the API to respond...' : 'No check has run yet')}</span>
                        </div>
                        <div className="admin-health-endpoint">
                            <Globe className="h-4 w-4" />
                            <code>GET {HEALTH_URL}</code>
                        </div>
                    </section>

                    <div className="admin-metric-grid admin-health-metrics">
                        {metrics.map(({ label, value, note, icon: Icon }) => (
                            <div key={label} className="admin-metric-card">
                                <div className="admin-metric-icon">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <div className="min-w-0">
                                    <span>{label}</span>
                                    <strong className="admin-health-metric-value">{value}</strong>
                                    <p>{note}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </main>
        </div>
    )
}
